import React from "react";
import { Box, LinearProgress, Typography } from "@mui/material";
import { useIntl } from "react-intl";
import { EMPTY_VALUE } from "../cmmsFormat";
import { getReadingProgress } from "../scheduleReset";
import messages from "../messages";

// Mirrors CmmsRecurringSchedule.DefaultWarnAtPercent: what a schedule saved without its own
// threshold warns at.
const DEFAULT_WARN_AT_PERCENT = 80;

/*
 * How far a usage schedule has run against its interval: a bar, the percentage, and "Overdue"
 * once it is past.
 *
 * Presentational, and shared by the two tabs in the asset panel that draw it — the usage tab,
 * which fetches one schedule's consumption at a time, and the planned maintenance tab, which gets
 * every schedule under the node in one response. Sitting in the same panel, the two have to agree
 * about when a schedule turns amber, and a second copy is how they would stop agreeing.
 *
 * Amber at the schedule's own WarnAtPercent. The fixed 80 this replaced was right only for a
 * schedule left on the default, and wrong in the reassuring direction for one set lower.
 *
 * Returns the bar and the figure side by side with no wrapper, so they sit in the caller's row on
 * its gap.
 */
export default function ScheduleProgress({ reading, warnAtPercent, cmmsColors }) {
  const intl = useIntl();
  const { barValue, label, isOverdue } = getReadingProgress(reading);

  let barColor = cmmsColors.activeText;
  if (isOverdue) barColor = "error.main";
  else if (label != null && barValue >= (warnAtPercent ?? DEFAULT_WARN_AT_PERCENT)) barColor = "warning.main";

  return (
    <>
      <Box sx={{ width: 120, flexShrink: 0 }}>
        <LinearProgress
          variant="determinate"
          value={barValue}
          color="inherit"
          sx={{
            height: 8,
            borderRadius: 1,
            backgroundColor: cmmsColors.trackBg,
            "& .MuiLinearProgress-bar": { backgroundColor: barColor },
          }}
        />
      </Box>

      {/* A dash, not 0 %, when there is no percentage: a schedule with nothing measuring it has
          not run for zero hours, it has not been read. */}
      <Typography variant="body2" sx={{ color: isOverdue ? cmmsColors.dangerText : cmmsColors.text, fontWeight: 700, minWidth: 100, flexShrink: 0, textAlign: "right" }}>
        {label != null ? `${label}%` : EMPTY_VALUE}
        {isOverdue && (
          <Typography component="span" variant="body2" sx={{ color: cmmsColors.dangerText, fontWeight: 700, ml: 0.5 }}>
            {intl.formatMessage(messages.usageScheduleOverdue)}
          </Typography>
        )}
      </Typography>
    </>
  );
}
